import React, { useContext } from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import { format } from 'date-fns/format';
import { sv, enUS } from 'date-fns/locale';
import { Box, Container, Flex } from '@components/layout/Grid';
import {
  Title,
  SubTitle,
  TextWrapper,
  TextWrapperInner,
  VideoWrapper,
  Disclaimer,
} from '@ecosystems/landing_pages/hero/components';
import Hero, { Thumbnail } from '@ecosystems/landing_pages/hero/Hero';
import SvgCircleCheckmark from '@assets/CircleCheckmark';
import LanguageContext from '@lib/contexts/languageContext';

import {
  BundleCategoryKind,
  BundleEssentialFieldsFragment,
  BundleQuery,
} from '@gql/generated';

const Section = styled(Hero)`
  position: relative;
  flex-direction: column;
  width: 100%;
  padding: 30px 10px;

  ${TextWrapper} {
    width: 100%;
    padding: 20px 0 0;
    margin: 0 auto;
  }

  ${VideoWrapper} {
    width: 100%;

    ${Thumbnail} {
      aspect-ratio: 3/2;
      object-fit: contain;
      object-position: center center;
      border-radius: 8px;
    }
  }

  ${Disclaimer} {
    font-style: normal;
    margin-top: 30px;

    ul {
      display: flex;
      flex-direction: column;
      align-items: center;
      list-style: none;
      color: white;
      font-size: 16px;
      font-weight: 600;
      padding-left: 0;

      li {
        display: flex;
        align-items: center;
        margin-top: 10px;

        svg {
          fill: white;
          height: 14px;
          margin-right: 10px;
          margin-top: 4px;
        }
      }
    }
  }

  ${Title} {
    font-size: 28px;
    font-weight: bold;
    line-height: 1.4;
  }

  ${SubTitle} {
    max-width: 560px;
    font-size: 16px;
    line-height: 1.3;
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    flex-direction: row;

    ${TextWrapper} {
      width: 50%;

      ${TextWrapperInner} {
        margin: 0 auto;

        ${Title} {
          font-size: 32px;
        }
      }
    }

    ${VideoWrapper} {
      width: 50%;
      height: 100%;
      padding: 0 40px 0 0;
    }

    ${Disclaimer} {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      font-style: normal;
      margin-bottom: 0;
      padding: 14px 20px;
      background-color: rgba(17, 158, 221, 0.2);

      ul {
        max-width: 860px;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 0 auto;

        li {
          margin-top: 0;
          margin-right: 50px;
        }
      }
    }
  }
`;

const AccessUntilText = styled.span`
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  font-style: italic;
  line-height: 20px;

  svg {
    margin-right: 5px;
  }
`;

const dateLocal = {
  se: sv,
  fi: sv,
  no: enUS,
  en: enUS,
};

const AccessStatus = ({ accessUntil }) => {
  return (
    <>
      <Flex width={1} key="0" justifyContent="space-between">
        <AccessUntilText>
          <SvgCircleCheckmark />
          <Trans
            id="bundle.id_page.hero.access_until_text"
            values={{ accessUntil }}
          />
        </AccessUntilText>
      </Flex>
    </>
  );
};

type Props = {
  bundle: BundleEssentialFieldsFragment &
    Pick<BundleQuery['bundle'], 'footerCta'>;
};

export const BundleExtraSection = (props: Props) => {
  const lang = useContext(LanguageContext);

  const { bundle } = props;

  if (!bundle) {
    return null;
  }

  let actions = [];

  if (bundle?.bundleCategory !== BundleCategoryKind['ContentHub']) {
    const accessUntil = new Date(bundle?.userAccess?.accessUntil);
    actions = [
      <AccessStatus
        key="0"
        accessUntil={format(accessUntil, 'dd MMMM yyyy', {
          locale: dateLocal[lang],
        })}
      />,
    ];
  }

  return (
    <Container fullWidth style={{ backgroundColor: '#f7f7f7' }}>
      <Container>
        <Section
          image={bundle?.footerCta?.image ?? bundle?.heroImage}
          title={bundle.footerCta?.title ?? bundle?.title}
          titleAs="h2"
          subtitle={
            <Box
              dangerouslySetInnerHTML={{
                __html: bundle.footerCta?.description,
              }}
              mb={20}
            ></Box>
          }
          noSubtitleWrapper={false}
          actions={actions}
        />
      </Container>
    </Container>
  );
};
